import {assert} from 'Asserts';
import {
  MethodNode,
  getMethodComponentCid,
  getMethodComponentName,
  MaterialFlow,
} from 'procedures/ProcedureDomainTypes';
import {TaskDefinitionVersionSpecWithTaskSchemaFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {isMethodNode} from './MethodNodeUtils';

/**
 * Field group keys used to categorize procedure fields.
 * Keep in sync with ProcedureFieldGroupKey in
 * benchling/procedures/controller/processes_platform/procedure_method_task_execution_type_adapter.py
 */
export enum ProcedureFieldGroupKey {
  MATERIAL_INPUTS = 'MATERIAL_INPUTS',
  MATERIAL_OUTPUTS = 'MATERIAL_OUTPUTS',
  DATA_PARAMETERS = 'DATA_PARAMETERS',
  EQUIPMENT = 'EQUIPMENT',
}

/**
 * Keep in sync with the backend node adapter's fieldClientIdentifier format in
 * ParameterSettingToWorkflowFieldDefinitionMapper.resolve_field_client_identifier
 */
export function getFieldClientId(fieldGroupKey: ProcedureFieldGroupKey, fieldName: string): string {
  return `${fieldGroupKey}/${fieldName}`;
}

/**
 * Extracts the material name from a field client identifier by removing the field group key prefix.
 *
 * Field client IDs follow the format `{fieldGroupKey}/{materialName}` (e.g., "MATERIAL_OUTPUTS/product").
 * This function validates that the ID starts with the expected group key prefix and returns the material name portion.
 *
 * @param fieldClientId - The field client identifier in the format `{fieldGroupKey}/{materialName}`
 * @param fieldGroupKey - The expected field group key prefix (e.g., MATERIAL_INPUTS or MATERIAL_OUTPUTS)
 * @returns The material name extracted from the field client ID
 */
function extractMaterialNameFromFieldClientId(
  fieldClientId: string,
  fieldGroupKey: ProcedureFieldGroupKey
): string {
  assert(
    fieldClientId.startsWith(`${fieldGroupKey}/`),
    `Expected field client id '${fieldClientId}' to start with the field group key '${fieldGroupKey}'`
  );
  return fieldClientId.slice(fieldGroupKey.length + 1);
}

/**
 * Extracts material flows from a process flowchart by analyzing DATA_FLOW edges between method nodes.
 *
 * This function iterates through all edges in the flowchart and identifies material flows by:
 * 1. Finding DATA_FLOW edges that connect method nodes
 * 2. Extracting material output settings from the source method node
 * 3. Extracting material input settings from the target method node
 * 4. Creating MaterialFlow objects that capture the connection between output and input materials
 *
 * @param flowchart - The process flowchart containing nodes and edges with task definition specs
 * @returns An array of MaterialFlow objects representing material connections between methods
 */
export function getMaterialFlowsFromProcessFlowchart(
  flowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
): Array<MaterialFlow> {
  const materialFlows: Array<MaterialFlow> = [];
  for (const edge of flowchart.edges) {
    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      // Find source method
      const sourceNode = flowchart.nodes[edge.fromNodeClientId];
      assert(isMethodNode(sourceNode), 'Expected source node for material edge to be a method node');
      const sourceMethod = sourceNode.newProcessTaskDefinitionVersion.applicationData as MethodNode;

      // Find target method
      const targetNode = flowchart.nodes[edge.toNodeClientId];
      assert(isMethodNode(targetNode), 'Expected target node for material edge to be a method node');
      const targetMethod = targetNode.newProcessTaskDefinitionVersion.applicationData as MethodNode;

      // Find source material
      assert(
        edge.fromFieldClientId != null,
        'Expected source field client id to be present for data flow edge'
      );
      const sourceMaterialName = extractMaterialNameFromFieldClientId(
        edge.fromFieldClientId,
        ProcedureFieldGroupKey.MATERIAL_OUTPUTS
      );
      const sourceMaterial = sourceMethod.materialOutputSettings.find(
        (setting) => getMethodComponentName(setting) === sourceMaterialName
      );
      assert(
        sourceMaterial != null,
        `Expected material output '${sourceMaterialName}' to exist on source method '${sourceMethod.name}'`
      );

      // Find target material
      assert(
        edge.toFieldClientId != null,
        'Expected target field client id to be present for data flow edge'
      );
      const targetMaterialName = extractMaterialNameFromFieldClientId(
        edge.toFieldClientId,
        ProcedureFieldGroupKey.MATERIAL_INPUTS
      );
      const targetMaterial = targetMethod.materialInputSettings.find(
        (setting) => getMethodComponentName(setting) === targetMaterialName
      );
      assert(
        targetMaterial != null,
        `Expected material input '${targetMaterialName}' to exist on target method '${targetMethod.name}'`
      );

      materialFlows.push({
        sourceMaterialCid: getMethodComponentCid(sourceMaterial),
        sourceMaterialName,
        sourceMethodName: sourceMethod.name,
        targetMaterialCid: getMethodComponentCid(targetMaterial),
        targetMaterialName,
        targetMethodName: targetMethod.name,
      });
    }
  }
  return materialFlows;
}
